import React, { useState, useEffect } from "react";
import { useGate } from "../lib/gate.jsx";
import { useTheme } from "../lib/theme.jsx";
import { USERS } from "../lib/users";
import { Sun, Moon, Lock, AlertCircle, Loader2, User as UserIcon, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";
import { APP_RELEASE } from "../lib/version";

export default function GatePage() {
  const { signIn } = useGate();
  const { theme, toggle } = useTheme();
  const [employeeId, setEmployeeId] = useState("");
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [showRoster, setShowRoster] = useState(false);

  // ambient ticker
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT(x => x + 1), 80);
    return () => clearInterval(id);
  }, []);

  function submit(e) {
    e?.preventDefault();
    setError("");
    setVerifying(true);
    setTimeout(() => {
      const result = signIn(employeeId, passcode);
      if (!result.ok) {
        setError(result.error || "Invalid credentials.");
        setVerifying(false);
      }
    }, 600);
  }

  return (
    <div className="min-h-screen bg-bg text-fg flex items-stretch" data-testid="gate-page">
      {/* Left — brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] xl:w-[42%] px-12 py-10 border-r border-border relative overflow-hidden grid-bg">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 30%, rgb(var(--primary) / 0.08), transparent 60%)" }} />

        <div className="relative z-10 flex items-center gap-3">
          <img src="/1rotary-logo.png" alt="1Rotary" className="h-12 w-auto" />
          <div className="leading-tight">
            <div className="font-display font-semibold tracking-tight text-base">1Rotary</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted">Executive Control Tower</div>
          </div>
        </div>

        <div className="relative z-10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-3">
            <span className="live-dot inline-block mr-2 align-middle" />
            {APP_RELEASE} · Role-Based Access
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight">
            Cooling the Nation,
            <br />
            <span className="italic text-primary">Creating Opportunities.</span>
          </h1>
          <p className="text-sm text-muted mt-4 max-w-md leading-relaxed">
            Each authorized executive sees a tailored view — Control Tower domains, AI Layer, Digital Twin, and KPI Framework data are scoped to your role and operating responsibilities.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {[
              { l: "Branches", v: "86" },
              { l: "Stockout SKUs", v: "312" },
              { l: "Forecast Acc.", v: "78.2%" },
            ].map((k, i) => (
              <div key={k.l} className="border-l-2 border-primary/40 pl-3">
                <div className="kpi-label">{k.l}</div>
                <div className="kpi-value text-lg mt-1">
                  {k.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted">
          <span>1Rotary Trading Corporation · Taguig, PH</span>
          <span className="font-mono-tabular">
            {new Date().toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })} PH
          </span>
        </div>
      </div>

      {/* Right — credential challenge */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 relative">
        <button
          onClick={toggle}
          data-testid="gate-theme-toggle"
          className="absolute top-5 right-5 h-9 w-9 inline-flex items-center justify-center border border-border bg-surface-2 hover:bg-surface rounded-sm"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
        </button>

        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/1rotary-logo.png" alt="1Rotary" className="h-12 w-auto" />
            <div>
              <div className="font-display font-semibold">1Rotary</div>
              <div className="text-[10px] uppercase tracking-widest text-muted">Control Tower</div>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-2 inline-flex items-center gap-2">
            <Lock size={11} /> Executive Access
          </div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">Sign in with your Employee ID</h2>
          <p className="text-sm text-muted mt-2 leading-relaxed">
            This is a restricted operational environment. Enter your 1Rotary Employee ID and one-time passcode issued by IT.
          </p>

          <div className="mt-7 space-y-4">
            <div>
              <label className="kpi-label block mb-1.5" htmlFor="gate-employee-id">Employee ID</label>
              <input
                id="gate-employee-id"
                data-testid="gate-employee-id"
                type="text"
                autoFocus
                autoComplete="username"
                value={employeeId}
                onChange={e => setEmployeeId(e.target.value)}
                placeholder="1R-CEO-0001"
                className="w-full h-11 px-3 bg-surface-2 border border-border focus:border-primary focus:ring-1 focus:ring-primary/40 outline-none rounded-sm text-sm font-mono-tabular tracking-wider transition-colors uppercase"
              />
            </div>
            <div>
              <label className="kpi-label block mb-1.5" htmlFor="gate-passcode">Passcode</label>
              <input
                id="gate-passcode"
                data-testid="gate-passcode"
                type="password"
                value={passcode}
                onChange={e => setPasscode(e.target.value)}
                placeholder="••••••"
                maxLength={12}
                className="w-full h-11 px-3 bg-surface-2 border border-border focus:border-primary focus:ring-1 focus:ring-primary/40 outline-none rounded-sm text-sm font-mono-tabular tracking-[0.4em] transition-colors uppercase"
              />
              <div className="mt-1.5 text-[10px] text-subtle font-mono-tabular">
                Passcodes are 6 characters · case-insensitive. Contact IT if locked out.
              </div>
            </div>

            {error && (
              <div data-testid="gate-error" className="flex items-start gap-2 text-xs text-critical bg-critical/10 border border-critical/30 px-3 py-2 rounded-sm">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid="gate-submit"
              disabled={verifying || !employeeId || !passcode}
              className={cn(
                "w-full h-11 rounded-sm text-sm font-semibold tracking-wide transition-all flex items-center justify-center gap-2",
                verifying || !employeeId || !passcode
                  ? "bg-surface-2 text-muted cursor-not-allowed border border-border"
                  : "bg-primary text-primary-fg hover:opacity-90 glow-primary"
              )}
            >
              {verifying ? (
                <><Loader2 size={14} className="animate-spin" /> Verifying…</>
              ) : (
                <>Enter Control Tower →</>
              )}
            </button>

            <button
              type="button"
              data-testid="gate-roster-toggle"
              onClick={() => setShowRoster(s => !s)}
              className="w-full text-[11px] text-muted hover:text-primary transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <UserIcon size={11} />
              {showRoster ? "Hide authorized roles" : "View authorized roles (8)"}
            </button>

            {showRoster && (
              <div data-testid="gate-roster" className="space-y-1.5 max-h-64 overflow-y-auto p-3 bg-surface-2/60 border border-border rounded-sm">
                {USERS.map(u => (
                  <button
                    key={u.employeeId}
                    type="button"
                    data-testid={`gate-roster-${u.role}`}
                    onClick={() => setEmployeeId(u.employeeId)}
                    className="w-full text-left text-[11px] flex items-center justify-between px-2 py-1.5 hover:bg-surface rounded-sm group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono-tabular text-primary shrink-0">{u.employeeId}</span>
                      <span className="text-muted truncate">{u.roleShort}</span>
                    </div>
                    <ChevronRight size={11} className="text-subtle group-hover:text-primary shrink-0" />
                  </button>
                ))}
                <div className="text-[9px] text-subtle pt-1.5 border-t border-border italic">
                  Click an entry to pre-fill Employee ID. Passcodes issued to authorized personnel only.
                </div>
              </div>
            )}
          </div>

          <div className="mt-8 pt-5 border-t border-border text-[10px] uppercase tracking-[0.18em] text-subtle flex items-center justify-between">
            <span>Session secured · TLS 1.3</span>
            <span className="font-mono-tabular">
              {String((t * 7) % 1000).padStart(3, "0")}-{String((t * 13) % 1000).padStart(3, "0")}
            </span>
          </div>

          <div className="mt-6 text-center text-[11px] text-muted">
            Developed by{" "}
            <a href="https://www.asterisk.com.sg" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              Asterisk Data and AI Labs
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
